// Widget-local types. API-contract shapes come from @apc/shared-types (type-only import — erased
// at build time, zero runtime cost, but keeps the widget's understanding of the API in sync with
// the backend instead of a hand-maintained duplicate).
import type { Finding, ScanUrlRole, ScanUrlStatus } from "@apc/shared-types";

export type { Finding, ScanUrlRole, ScanUrlStatus };

export interface FormUrlEntry {
  url: string;
  role: ScanUrlRole;
}

/** One row of the scanning-state per-URL progress list (prd.md §2.2, S3/S3a-e). */
export interface UrlProgress {
  urlId: string;
  url: string;
  status: ScanUrlStatus;
  errorCode: string | null;
  partial: boolean;
  score: number | null;
}

export type EmailGateTrigger = "during_scan" | "post_report";

export interface EmailGateState {
  status: "closed" | "open" | "submitting" | "success" | "error";
  trigger: EmailGateTrigger | null;
  errorMessage: string | null;
}

/**
 * The main flow state — prd.md §2's S0-S7 state machine. S0/S1 (empty form / inline
 * validation) are collapsed into one "form" state with a `validationError` field, since S1 is
 * a validation overlay on the same screen, not a distinct one. The email gate (S5/S5a/S5b) is
 * modeled separately (see EmailGateState) because prd.md §2.3 describes it as an overlay that
 * can trigger from *either* "scanning" or "report" and returns to the state it came from, not
 * as a state that replaces them.
 */
export type MainState =
  | { kind: "form"; validationError: string | null }
  | { kind: "submitting" }
  | { kind: "submit_error"; message: string } // F1
  | { kind: "scanning"; scanId: string; domain: string; urls: UrlProgress[] } // S3/S3a-e
  | { kind: "all_failed"; scanId: string; urls: UrlProgress[] } // F4
  | { kind: "report"; scanId: string; domain: string; urls: UrlProgress[]; domainScore: number | null; findings: Finding[] } // S4
  | { kind: "permalink_not_found" }; // S6, unknown/expired job id

export interface WidgetState {
  main: MainState;
  emailGate: EmailGateState;
}

export function initialWidgetState(): WidgetState {
  return {
    main: { kind: "form", validationError: null },
    emailGate: { status: "closed", trigger: null, errorMessage: null },
  };
}
